import type { ButtonHTMLAttributes, ReactNode } from 'react';

type BotonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variante?: Variante;
  /** El paciente usa la escala grande; el equipo, en escritorio, la compacta (10.7). */
  escala?: Escala;
  cargando?: boolean;
};

const BASE =
  'inline-flex items-center justify-center gap-2 font-bold transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const POR_ESCALA = {
  paciente: 'min-h-15 w-full rounded-lg px-6 text-touch',
  // La acción secundaria dentro de una tarjeta del paciente: 48 px, no ancho completo.
  chica: 'min-h-12 self-start rounded-lg px-5 text-lg',
  equipo: 'min-h-11.5 rounded-md px-6 text-md',
} as const;

// E3.8.2: el primario lleva el brillo navy de la referencia; el de texto deja
// de ser un enlace subrayado y pasa a botón fantasma, con el mismo alto.
const POR_VARIANTE = {
  primario: 'bg-brand text-primary-foreground shadow-glow-navy hover:bg-brand-strong',
  secundario: 'border-2 border-primary bg-card text-link hover:bg-brand-soft',
  texto: 'bg-transparent text-link hover:bg-brand-soft',
  // Texto navy-dark: blanco sobre teal da 2,40:1 y no pasa AA.
  teal: 'bg-accent text-heading hover:bg-accent-soft',
} as const;

type Variante = keyof typeof POR_VARIANTE;
type Escala = keyof typeof POR_ESCALA;

/**
 * Las clases del botón, para un enlace que tiene que verse como uno: el que
 * abre la aplicación autenticadora es un `<a href="otpauth://…">`, no un botón.
 */
export function clasesDeBoton({
  variante = 'primario',
  escala = 'paciente',
}: {
  variante?: Variante;
  escala?: Escala;
} = {}): string {
  return `${BASE} ${POR_ESCALA[escala]} ${POR_VARIANTE[variante]}`;
}

/**
 * Botón del sistema.
 *
 * Tres decisiones deliberadas para el público de esta app —adultos mayores
 * recién operados, usando un celular con una mano—:
 *
 *   - **60px de alto**, muy por encima de los 44 que pide WCAG 2.1 AA. El target
 *     grande no es estética: es la diferencia entre acertar y no acertar.
 *   - **Ancho completo** en móvil, para no obligar a apuntar.
 *   - Cargando **no cambia el texto**: cambia el estado y agrega un indicador.
 *     Un botón cuyo texto muta bajo el dedo desorienta.
 */
export function Boton({
  children,
  variante = 'primario',
  escala = 'paciente',
  cargando = false,
  disabled,
  className = '',
  type = 'button',
  ...resto
}: BotonProps) {
  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      className={`${clasesDeBoton({ variante, escala })} ${className}`}
      disabled={disabled === true || cargando}
      aria-busy={cargando}
      {...resto}
    >
      {cargando ? (
        <span
          aria-hidden="true"
          className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {children}
    </button>
  );
}
